import { apiRequestJson } from "./queryClient";

// Fetch-based replacement for the historical axios instance. It preserves the
// exact legacy behavior of the old interceptors (see task #211 notes):
// - baseURL /api/crm and Content-Type application/json
// - Authorization Bearer token read from localStorage (fallback: in-memory)
// - on 401: one refresh-and-retry attempt; if the refresh fails, clear the
//   tokens and HARD redirect to /crm-secure/login (window.location.href).
// - errors expose axios-compatible `error.response.status` and
//   `error.response.data` because several CRM pages read
//   `err.response?.data?.error` / `err.response?.status`.
// The canonical transport with soft logout remains `queryClient.ts`
// (`fetchWithAuthRetry`); this module stays the legacy path for the typed
// helpers below.

const BASE_URL = "/api/crm";

let authToken: string | null = null;

export interface CrmApiError extends Error {
  response?: { status: number; data: any };
}

function buildError(status: number, data: any): CrmApiError {
  const message =
    (data && typeof data === "object" && (data.error || data.message)) ||
    `Request failed with status code ${status}`;
  const err = new Error(message) as CrmApiError;
  err.response = { status, data };
  return err;
}

async function parseBody(res: Response): Promise<any> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function rawRequest(
  method: string,
  path: string,
  body?: unknown,
  token?: string | null,
): Promise<Response> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const bearer = token ?? (localStorage.getItem("crm_token") || authToken);
  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  return fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

async function request<T = any>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  let res = await rawRequest(method, path, body);

  // Legacy 401 handling: one refresh-and-retry, hard redirect on failure.
  if (res.status === 401 && !path.startsWith("/auth/refresh")) {
    const refreshToken = localStorage.getItem("crm_refresh_token");
    if (refreshToken) {
      try {
        const refreshRes = await rawRequest("POST", "/auth/refresh", {
          refreshToken,
        });
        if (!refreshRes.ok) {
          throw buildError(refreshRes.status, await parseBody(refreshRes));
        }
        const refreshData = await parseBody(refreshRes);
        setAuthToken(refreshData.token);
        localStorage.setItem("crm_token", refreshData.token);
        localStorage.setItem("crm_refresh_token", refreshData.refreshToken);

        // Retry original request with the new token
        res = await rawRequest(method, path, body, refreshData.token);
      } catch {
        // Refresh failed, redirect to login (legacy hard redirect)
        clearAuthToken();
        localStorage.removeItem("crm_token");
        localStorage.removeItem("crm_refresh_token");
        window.location.href = "/crm-secure/login";
        throw buildError(401, { error: "Sessione scaduta" });
      }
    }
  }

  const data = await parseBody(res);
  if (!res.ok) throw buildError(res.status, data);
  return data as T;
}

// Minimal axios-like facade kept for existing `import { crmApi }` callers.
export const crmApi = {
  get: async <T = any>(path: string) => ({ data: await request<T>("GET", path) }),
  post: async <T = any>(path: string, body?: unknown) => ({ data: await request<T>("POST", path, body) }),
  patch: async <T = any>(path: string, body?: unknown) => ({ data: await request<T>("PATCH", path, body) }),
  put: async <T = any>(path: string, body?: unknown) => ({ data: await request<T>("PUT", path, body) }),
  delete: async <T = any>(path: string) => ({ data: await request<T>("DELETE", path) }),
};

// Auth token management
export const setAuthToken = (token: string) => {
  authToken = token;
};

export const clearAuthToken = () => {
  authToken = null;
};

// Authentication endpoints
export const login = async (email: string, password: string, rememberMe: boolean = false) => {
  return request("POST", "/auth/login", { email, password, rememberMe });
};

export const requestPasswordReset = async (email: string) => {
  return request("POST", "/auth/forgot-password", { email });
};

export const resetPassword = async (token: string, newPassword: string) => {
  return request("POST", "/auth/reset-password", { token, newPassword });
};

export const refreshAuthToken = async (refreshToken: string) => {
  return request("POST", "/auth/refresh", { refreshToken });
};

export const logout = async () => {
  return request("POST", "/auth/logout");
};

export const getCurrentUser = async () => {
  return request("GET", "/auth/me");
};

export const changePassword = async (currentPassword: string, newPassword: string) => {
  return request("POST", "/auth/change-password", { currentPassword, newPassword });
};

export const forceChangePassword = async (newPassword: string, forwardEmail?: string) => {
  return request("POST", "/auth/force-change-password", { newPassword, forwardEmail });
};

export const webauthnCheckCredentials = async (email: string) => {
  return request("POST", "/auth/webauthn/check", { email });
};

export const webauthnRegisterOptions = async () => {
  return request("POST", "/auth/webauthn/register-options");
};

export const webauthnRegisterVerify = async (credential: any, deviceName?: string) => {
  return request("POST", "/auth/webauthn/register-verify", { credential, deviceName });
};

export const webauthnAuthOptions = async (email: string) => {
  return request("POST", "/auth/webauthn/auth-options", { email });
};

export const webauthnAuthVerify = async (email: string, credential: any) => {
  return request("POST", "/auth/webauthn/auth-verify", { email, credential });
};

export const webauthnGetCredentials = async () => {
  return request("GET", "/auth/webauthn/credentials");
};

export const webauthnDeleteCredential = async (id: string) => {
  return request("DELETE", `/auth/webauthn/credentials/${id}`);
};

export const createStaffUsers = async () => {
  return request("POST", "/staff-profiles/create-users");
};

// Admin Users Management
export const getAdminUsers = async () => {
  return request("GET", "/admin-users");
};

export const createAdminUser = async (userData: any) => {
  return request("POST", "/admin-users", userData);
};

// Customers Management
export const getCustomers = async () => {
  return request("GET", "/customers");
};

export const getCustomerById = async (id: string) => {
  return request("GET", `/customers/${id}`);
};

export const getCustomerPurchaseDeeds = async (id: string) => {
  return request("GET", `/customers/${id}/purchase-deeds`);
};

export const createCustomer = async (customerData: any) => {
  return request("POST", "/customers", customerData);
};

export const updateCustomer = async (id: string, customerData: any) => {
  return request("PATCH", `/customers/${id}`, customerData);
};

export const deleteCustomer = async (id: string) => {
  return request("DELETE", `/customers/${id}`);
};

// Transactions Management
export const getTransactions = async () => {
  return request("GET", "/transactions");
};

export const createTransaction = async (transactionData: any) => {
  return request("POST", "/transactions", transactionData);
};

// Purchase Acts
export const getPurchaseActs = async () => {
  return request("GET", "/purchase-acts");
};

export const createPurchaseAct = async (actData: any) => {
  return request("POST", "/purchase-acts", actData);
};

// Sale Acts
export const getSaleActs = async () => {
  return request("GET", "/sale-acts");
};

export const createSaleAct = async (actData: any) => {
  return request("POST", "/sale-acts", actData);
};

// Lead Management
export const getContacts = async () => {
  return request("GET", "/contacts");
};

export const getGoldEvaluations = async () => {
  return request("GET", "/gold-evaluations");
};

export const getWatchSells = async () => {
  return request("GET", "/watch-sells");
};

export const getInvestmentRequests = async () => {
  return request("GET", "/investment-requests");
};

// Statistics & Analytics
export const getDashboardStats = async () => {
  return request("GET", "/stats/dashboard");
};

export const getRevenueStats = async (period: "day" | "week" | "month" | "year") => {
  return request("GET", `/stats/revenue?period=${period}`);
};

/**
 * Adapter di compatibilità per i chiamanti `import * as crmApi from "@/lib/crm-api"`
 * che usano la vecchia firma `apiRequest(url, options?)`. NON è più un transport
 * duplicato: si limita a normalizzare la chiamata e a delegarla al transport
 * unificato (`apiRequestJson` in `queryClient`), così il refresh-and-retry del
 * token vale anche per queste pagine. Le pagine migrate usano direttamente
 * `apiRequestJson("METHOD", url, data)`.
 */
export const apiRequest = async <T = any>(
  url: string,
  options?: RequestInit,
): Promise<T> => {
  const method = (options?.method ?? "GET").toUpperCase();
  let data: unknown | undefined;
  if (options?.body != null) {
    data =
      typeof options.body === "string"
        ? JSON.parse(options.body)
        : options.body;
  }
  return apiRequestJson<T>(method, url, data);
};
